import * as React from "react";
import Money from "web_modules/Money";
import * as moment from "moment";
import APIClient from "web_modules/APIClient";
import classnames from "classnames";
import { convertTimeFrame } from "./helpers";
import { getFileDownloadBaseURL } from "../../../config";

const styles = require("./styles.module.scss");

interface PreApprovalsProps {
  preApprovals: any;
  match: any;
}

function displayLetters(letters) {
  if (letters.length === 0) {
    return <div className={styles.letterContainer}>No letters</div>;
  }

  return (
    <>
      {letters.map((letter, index) => (
        <div key={letter.s3_key} className={styles.letterContainer}>
          <p>Letter #{`${index + 1}`}</p>
          <ul className={classnames(["list-group", "list-group-flush", styles.mediumWidth])}>
            <li className="list-group-item">
              <p>
                Downpayment: &nbsp;
                <Money value={letter.downpayment} decimals={false} />
              </p>
              <p>
                Loan Amount: &nbsp;
                <Money value={letter.loan_amount} decimals={false} />
              </p>
              <p>Created At: &nbsp; {moment(letter.created_at).format("HH:MM:SS MMM DD, Y ")}</p>
              <p>
                <a
                  href={getFileDownloadBaseURL(letter.s3_key)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Letter
                </a>
              </p>
            </li>
          </ul>
        </div>
      ))}
      <hr />
    </>
  );
}

// value can be a string or a react element
function displayListItem(label: string, value: string | JSX.Element) {
  return (
    <li className="list-group-item" key={label}>
      {label}:<span style={{ fontWeight: 600 }}> {value}</span>
    </li>
  );
}

function displayCoBorrowerDetails(pa: any) {
  return (
    <>
      <h3>Coborrower Info: </h3>
      <ul className={classnames(["list-group", "list-group-flush", styles.mediumWidth])}>
        {displayListItem("Full Name", `${pa.borrower2_first_name} ${pa.borrower2_last_name}`)}
      </ul>
    </>
  );
}

class PreApprovalDetail extends React.Component<PreApprovalsProps, any> {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      preapproval: null,
    };
  }

  componentDidMount() {
    const {
      match: {
        params: { preapprovalID },
      },
    } = this.props;

    APIClient.get(`/uw/pre_approval/${preapprovalID}`).then((resp: any) => {
      this.setState({ loading: false, preapproval: resp.pre_approval_details });
    });
  }

  render() {
    // NOTE: doing in-line styles here since this page was made quickly and has a near-future deprecation date
    const { preapproval: pa, loading } = this.state;

    if (loading) {
      return (
        <div className={styles.homepage}>
          <h3>loading details ...</h3>
        </div>
      );
    }

    return (
      <div className={styles.homepage}>
        <h2>
          Preapproval Details for <span style={{ fontWeight: 800 }}>{pa.borrower1_first_name}</span>
        </h2>
        <h3>Personal Info: </h3>
        <ul className={classnames(["list-group", "list-group-flush", styles.mediumWidth])}>
          {[
            {
              label: "Name",
              value: `${pa.borrower1_first_name} ${pa.borrower1_last_name}`,
            },
            {
              label: "Phone",
              value: `${pa.borrower1_phone}`,
            },
            {
              label: "DOB",
              value: `${moment(pa.borrower1_dob).format("MMMM D, Y")}`,
            },
            {
              label: "Relationship status",
              value: `${pa.borrower1_relationship_status}`,
            },
            {
              label: "Has a coborrower",
              value: pa.has_borrower2 ? "Yes" : "No",
            },
            {
              label: "Soft Credit Score",
              value: `${pa.credit_score}`,
            },
          ].map(({ label, value }) => displayListItem(label, value))}
        </ul>

        <h3>Loan Info:</h3>
        <ul className={classnames(["list-group", "list-group-flush", styles.mediumWidth])}>
          {[
            {
              label: "Estimated Purchase Price",
              value: <Money value={pa.borrower_estimated_purchase_price} decimals={false} />,
            },
            {
              label: "Estimated Down Payment",
              value: <Money value={pa.borrower_estimated_down_payment} decimals={false} />,
            },
            {
              label: "Existing Savings",
              value: <Money value={pa.borrower_existing_savings} decimals={false} />,
            },
            {
              label: "Max Loan Amount",
              value: <Money value={pa.max_loan_amount} decimals={false} />,
            },
          ].map(({ label, value }) => displayListItem(label, value))}
        </ul>

        <h3>Letters: </h3>
        {displayLetters(pa.letters)}

        <h3>Loan Info:</h3>
        <ul className={classnames(["list-group", "list-group-flush", styles.mediumWidth])}>
          {[
            {
              label: "Estimated Purchase Price",
              value: <Money value={pa.borrower_estimated_purchase_price} decimals={false} />,
            },
            {
              label: "Estimated Down Payment",
              value: <Money value={pa.borrower_estimated_down_payment} decimals={false} />,
            },
            {
              label: "Existing Savings",
              value: <Money value={pa.borrower_existing_savings} decimals={false} />,
            },
            {
              label: "Purchase Time Frame",
              value: convertTimeFrame(pa.purchase_timeframe),
            },
          ].map(({ label, value }) => displayListItem(label, value))}
        </ul>

        <h3>Property Info:</h3>
        <ul className={classnames(["list-group", "list-group-flush", styles.mediumWidth])}>
          {[
            {
              label: "Property Location",
              value: `${pa.property_city}, ${pa.property_state}`,
            },
            {
              label: "Purchase stage",
              value: `${pa.purchase_stage}`,
            },
            {
              label: "Property usage",
              value: `${pa.property_usage}`,
            },
            {
              label: "Property type",
              value: `${pa.property_type}`,
            },
          ].map(({ label, value }) => displayListItem(label, value))}
        </ul>

        {pa.has_borrower2 && displayCoBorrowerDetails(pa)}
      </div>
    );
  }
}

export default PreApprovalDetail;
